/**
 * The presentation's prizes (TIBBS-VOICE.md 4), in the order Tibbs presents
 * them, and the words the podium listens for. One source for the prompt, the
 * fallback writer, the server-side check and the podium, so a prize can never
 * be named one way in the script and listened for another way on the table.
 */
import { captionText } from './script';

export type PrizeKey = 'last' | 'bronze' | 'silver' | 'gold';

/** Last place's prize: an ancient disaster, presented as though it were an honor. */
export const LAST_PRIZE = {
  /** On the podium label. */
  name: 'Pompeii Prize',
  /** In the script, word for word: the podium listens for it. */
  spoken: 'the Pompeii Prize',
  /** What the winner takes home. */
  object: 'a lump of lava rock, still warm',
} as const;

/** How each prize is said, in presentation order. */
export const PRIZE_NAMES: Record<PrizeKey, string> = {
  last: LAST_PRIZE.spoken,
  bronze: 'the Bronze',
  silver: 'the Silver Laurel',
  gold: 'the Golden Amphora',
};

/** The words that raise each podium step, in presentation order. */
export const PRIZE_CUES: readonly (readonly [PrizeKey, RegExp])[] = [
  ['last', /pompeii prize|lava rock/i],
  ['bronze', /\bbronze\b/i],
  ['silver', /silver laurel|\bsilver\b/i],
  ['gold', /golden amphora|\bamphora\b|champion/i],
];

/** Sentences of a script (captions), in order. */
export function scriptSentences(texts: string[]): string[] {
  const out: string[] = [];
  for (const t of texts) for (const p of String(t).match(/[^.!?…]*[.!?…]+["')\]]*\s*|[^.!?…]+$/g) ?? []) {
    const c = captionText(p);
    if (c) out.push(c);
  }
  return out;
}

/**
 * The sentence that presents each prize someone won: prizes are found in
 * presentation order, each after the one before, skipping the opener's two
 * sentences (the second names the Golden Amphora). A prize missing from the
 * result is never said, or said out of order.
 */
export function prizeCues(sentences: string[], won: (prize: PrizeKey) => boolean): Partial<Record<PrizeKey, number>> {
  const at: Partial<Record<PrizeKey, number>> = {};
  let from = Math.min(2, sentences.length);
  for (const [prize, re] of PRIZE_CUES) {
    if (!won(prize)) continue;
    const i = sentences.findIndex((s, k) => k >= from && re.test(s));
    if (i < 0) continue;
    at[prize] = i;
    from = i + 1;
  }
  return at;
}
